import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { Field } from "../../components/neu/Field";
import { LocationPicker } from "../map/LocationPicker";
import { createDuty } from "../../api/duties";
import { DutyEmployees } from "./DutyEmployees";
import { EMPTY_DUTY } from "./dutyDefaults";

export const DutyForm = ({ employees, onCancel, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_DUTY);
  const [saving, setSaving] = useState(false);
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggle = (id) =>
    setForm((prev) => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(id)
        ? prev.employee_ids.filter((x) => x !== id)
        : [...prev.employee_ids, id],
    }));

  const save = async () => {
    if (!form.title || !form.date || form.employee_ids.length === 0) return toast.error(t("duty.missing"));
    setSaving(true);
    try {
      await createDuty({ ...form, radius_m: Number(form.radius_m), end_date: form.end_date || null });
      toast.success(t("duty.created"));
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <NeuCard className="grid gap-4 p-6" data-testid="duty-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("duty.titleField")} testId="duty-title" value={form.title} onChange={set("title")} />
        <Field label={t("duty.address")} testId="duty-address" value={form.address} onChange={set("address")} />
        <Field label={t("duty.date")} testId="duty-date" type="date" value={form.date} onChange={set("date")} />
        <Field label={t("duty.endDate")} testId="duty-end-date" type="date" value={form.end_date} onChange={set("end_date")} />
        <Field label={t("duty.startTime")} testId="duty-start" type="time" value={form.start_time} onChange={set("start_time")} />
        <Field label={t("duty.endTime")} testId="duty-end" type="time" value={form.end_time} onChange={set("end_time")} />
        <Field label={t("duty.radius")} testId="duty-radius" type="number" value={form.radius_m} onChange={set("radius_m")} />
        <Field label={t("duty.notes")} testId="duty-notes" value={form.notes} onChange={set("notes")} />
      </div>

      <DutyEmployees employees={employees} selected={form.employee_ids} onToggle={toggle} />

      <LocationPicker
        latitude={form.latitude}
        longitude={form.longitude}
        radiusM={Number(form.radius_m) || 150}
        height="h-72"
        onChange={({ latitude, longitude, label }) =>
          setForm((prev) => ({ ...prev, latitude, longitude, address: label || prev.address }))
        }
      />

      <div className="flex gap-2">
        <NeuButton variant="primary" className="flex-1" data-testid="duty-save" disabled={saving} onClick={save}>
          {t("duty.save")}
        </NeuButton>
        <NeuButton data-testid="duty-cancel" onClick={onCancel}>
          {t("app.cancel")}
        </NeuButton>
      </div>
    </NeuCard>
  );
};
